import {useEquipmentPerformances} from '@domain/kpis'
import {
  EquipmentCategory,
  EquipmentPerformance,
  KpiVisibilityFlag,
  Shift
} from '@hconnect/common/types'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {Grid, Theme, useMediaQuery} from '@mui/material'
import {mapEquipmentPerformances} from '@widgets/kpis'
import {isEmpty, sortBy} from 'lodash'
import {FC} from 'react'

import {useTimeFrames} from '../../hooks'

import {PerformanceSummary} from './PerformanceSummary'

const HD_WIDTH_BREAKPOINT = 1366

const CATEGORIES_TO_DISPLAY_ON_LEFT = [
  EquipmentCategory.RAW,
  EquipmentCategory.CRUSHER,
  EquipmentCategory.COAL
]

const CATEGORIES_TO_DISPLAY_ON_RIGHT = [
  EquipmentCategory.CEMENT,
  EquipmentCategory.CLINKER,
  EquipmentCategory.UNKNOWN
]

const filterByCategories = (
  equipmentPerformances: EquipmentPerformance[] | undefined,
  categories: EquipmentCategory[]
) =>
  equipmentPerformances?.filter(
    (ep) => ep.equipment.equipmentCategory && categories.includes(ep.equipment.equipmentCategory)
  )

type EquipmentKpisProps = {
  shifts: Shift[]
}

export const EquipmentKpis: FC<EquipmentKpisProps> = ({shifts}) => {
  const {plantId} = useUpmPlantContext()
  const timezone = useUpmPlantTimezone()
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const isAtLeastHdResolution = useMediaQuery(({breakpoints}: Theme) =>
    breakpoints.up(HD_WIDTH_BREAKPOINT)
  )
  const {timeFrames} = useTimeFrames(shifts)
  const {data: equipmentPerformances, isLoading: isEquipmentPerformancesLoading} =
    useEquipmentPerformances(
      {
        plantId,
        timeFrames,
        kpiVisibilityFlag: KpiVisibilityFlag.ShiftSummaryPerformancePage
      },
      {
        select: (data) => sortBy(mapEquipmentPerformances(data, timezone), 'equipment.id')
      }
    )
  const useTwoGrids = isDesktop && isAtLeastHdResolution
  if (useTwoGrids) {
    const leftDataSet = filterByCategories(equipmentPerformances, CATEGORIES_TO_DISPLAY_ON_LEFT)
    const rightDataSet = filterByCategories(equipmentPerformances, CATEGORIES_TO_DISPLAY_ON_RIGHT)

    return (
      <Grid container spacing={2} width="100%">
        <>
          {!isEmpty(leftDataSet) && (
            <Grid item md={6}>
              <PerformanceSummary
                equipmentPerformances={leftDataSet}
                timeFrames={timeFrames}
                isLoading={isEquipmentPerformancesLoading}
                data-test-id="equipment-performance-summary-0"
                isDesktop={true}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <PerformanceSummary
              equipmentPerformances={rightDataSet}
              timeFrames={timeFrames}
              isLoading={isEquipmentPerformancesLoading}
              data-test-id="equipment-performance-summary-1"
              isDesktop={true}
            />
          </Grid>
        </>
      </Grid>
    )
  }

  return (
    <Grid container spacing={2} width="100%">
      <Grid item xs={12}>
        <PerformanceSummary
          equipmentPerformances={equipmentPerformances}
          timeFrames={timeFrames}
          isLoading={isEquipmentPerformancesLoading}
          data-test-id="equipment-performance-summary-0"
          isDesktop={isDesktop}
        />
      </Grid>
    </Grid>
  )
}
